import { ArrowRight, FileText, History, LogOut, ShieldCheck, Upload, UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { signOut, useAuth } from '../lib/auth';
import { formatDocTitle, parseDocChronoTimestamp } from '../lib/document-display';
import { formatUnitLabel, ownerUnits, pb } from '../lib/pb';

interface OwnerDocument {
  id: string;
  title: string;
  category: string;
  is_approved: boolean;
  processed_pdf: string;
  original_filename: string;
  created: string;
  collectionId: string;
}

const CATEGORY_LABEL: Record<string, string> = {
  legal: 'Compliance Reports',
  minutes: 'Meeting Minutes',
  financials: 'Financials',
  'president-report': "President's Report",
  'contract-bids': 'Contracts & Bids',
  emergency: 'Emergency Information',
  'owner-info': 'Owner Information',
  declaration: 'Governing Documents',
  misc: 'Other',
};

export function Portal(): JSX.Element {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [docs, setDocs] = useState<OwnerDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileToken, setFileToken] = useState<string>('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'Owner Portal | Sunset Harbor Condominiums';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Owners see everything except admin-only records: the full set of
        // public docs (governing docs, budget, minutes, etc.) plus the
        // owner-only roster. Avoids forcing the owner to bounce between
        // /portal and /documents to find a file.
        const result = await pb.collection('documents').getList<OwnerDocument>(1, 200, {
          filter: 'is_admin_only = false && status = "ready"',
          sort: '-created',
          fields:
            'id,title,category,is_approved,processed_pdf,original_filename,created,collectionId',
        });
        if (!cancelled) setDocs(result.items);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('We could not load the documents. Try refreshing in a moment.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    pb.files
      .getToken()
      .then((t) => {
        if (!cancelled) setFileToken(t);
      })
      .catch((err) => {
        console.error('file token fetch failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function fileUrl(doc: OwnerDocument): string {
    const opts = fileToken ? { download: true, token: fileToken } : { download: true };
    return pb.files.getURL({ ...doc, collectionId: doc.collectionId }, doc.processed_pdf, opts);
  }

  function onSignOut(): void {
    signOut();
    navigate('/', { replace: true });
  }

  // Group docs by category, then sort each group chronologically by the date
  // in the title (newest first) so meetings, budgets, and reports all land
  // top-down by what they're about, not by upload date.
  const grouped = (docs ?? []).reduce<Record<string, OwnerDocument[]>>((acc, d) => {
    (acc[d.category] ??= []).push(d);
    return acc;
  }, {});
  for (const cat of Object.keys(grouped)) {
    grouped[cat]?.sort(
      (a, b) => parseDocChronoTimestamp(b.title) - parseDocChronoTimestamp(a.title),
    );
  }
  const categoryOrder = [
    'declaration',
    'legal',
    'emergency',
    'owner-info',
    'minutes',
    'financials',
    'president-report',
    'contract-bids',
    'misc',
  ];
  const sortedCategories = categoryOrder.filter((c) => grouped[c]?.length);

  return (
    <>
      <PageHero
        eyebrow="Owners Portal"
        title={user?.name ? `Welcome, ${user.name.split(/[\s&]/)[0]}` : 'Owner Portal'}
        lead={
          ownerUnits(user).length > 0
            ? `${formatUnitLabel(ownerUnits(user))}. Owner-only records and records-request submission.`
            : 'Owner-only records and records-request submission.'
        }
      />

      <section className="section bg-brand-white">
        <Container>
          <div
            className={`mb-12 grid gap-4 ${isAdmin ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'}`}
          >
            <Link
              to="/contact?category=records-request"
              className="flex items-start gap-4 rounded-lg border border-brand-surface bg-brand-white p-6 shadow-card transition-shadow hover:shadow-card-hover"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                <FileText size={20} strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow-deep">
                  Records request
                </p>
                <p className="mt-1 text-base font-bold text-brand-charcoal">
                  Request copies under FS 718.111(12)(c)
                </p>
                <p className="mt-2 text-sm text-brand-body-medium">10 working days to respond.</p>
              </div>
            </Link>

            {isAdmin ? (
              <Link
                to="/admin/documents"
                className="flex items-start gap-4 rounded-lg border border-brand-charcoal bg-brand-charcoal p-6 text-brand-white shadow-card transition-shadow hover:shadow-card-hover"
              >
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                  <Upload size={20} strokeWidth={1.75} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow">
                    Admin
                  </p>
                  <p className="mt-1 text-base font-bold">Manage documents</p>
                  <p className="mt-2 text-sm text-brand-white/80">Upload, edit, delete records.</p>
                </div>
              </Link>
            ) : null}

            {isAdmin ? (
              <Link
                to="/admin/records-requests"
                className="flex items-start gap-4 rounded-lg border border-brand-charcoal bg-brand-charcoal p-6 text-brand-white shadow-card transition-shadow hover:shadow-card-hover"
              >
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                  <ShieldCheck size={20} strokeWidth={1.75} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow">
                    Admin
                  </p>
                  <p className="mt-1 text-base font-bold">Records-request queue</p>
                  <p className="mt-2 text-sm text-brand-white/80">
                    Review and fulfill owner requests.
                  </p>
                </div>
              </Link>
            ) : null}

            {isAdmin ? (
              <Link
                to="/admin/audit-log"
                className="flex items-start gap-4 rounded-lg border border-brand-charcoal bg-brand-charcoal p-6 text-brand-white shadow-card transition-shadow hover:shadow-card-hover"
              >
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                  <History size={20} strokeWidth={1.75} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow">
                    Admin
                  </p>
                  <p className="mt-1 text-base font-bold">Audit log</p>
                  <p className="mt-2 text-sm text-brand-white/80">
                    Sign-ins, role changes, document + request activity.
                  </p>
                </div>
              </Link>
            ) : null}

            {isAdmin ? (
              <Link
                to="/admin/users"
                className="flex items-start gap-4 rounded-lg border border-brand-charcoal bg-brand-charcoal p-6 text-brand-white shadow-card transition-shadow hover:shadow-card-hover"
              >
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                  <UserCog size={20} strokeWidth={1.75} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow">
                    Admin
                  </p>
                  <p className="mt-1 text-base font-bold">Owner accounts</p>
                  <p className="mt-2 text-sm text-brand-white/80">
                    Update owner emails and reset passwords.
                  </p>
                </div>
              </Link>
            ) : null}

            <button
              type="button"
              onClick={onSignOut}
              className="flex items-start gap-4 rounded-lg border border-brand-surface bg-brand-white p-6 text-left shadow-card transition-shadow hover:shadow-card-hover"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-surface text-brand-charcoal">
                <LogOut size={20} strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                  Session
                </p>
                <p className="mt-1 text-base font-bold text-brand-charcoal">Sign out</p>
                <p className="mt-2 text-sm text-brand-body-medium">{user?.email}</p>
              </div>
            </button>
          </div>

          <div className="mb-10 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
              Document library
            </p>
            <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
              All records available to signed-in owners
            </h2>
            <p className="mt-3 text-base leading-relaxed text-brand-body-dark">
              Governing documents, compliance reports, board minutes, the approved budget, and
              owner-only records like the unit-owner roster. Everything the association makes
              available to you, in one place.
            </p>
          </div>

          {error ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {error}
            </div>
          ) : docs === null ? (
            <p className="text-sm text-brand-body-medium">Loading...</p>
          ) : docs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-brand-surface bg-brand-surface/30 px-6 py-12 text-center">
              <p className="text-sm text-brand-body-medium">
                No documents have been posted yet. They will appear here once an admin uploads
                them.
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {sortedCategories.map((cat) => (
                <div key={cat}>
                  <h3 className="mb-5 text-sm font-semibold uppercase tracking-[0.18em] text-brand-yellow-deep">
                    {CATEGORY_LABEL[cat] ?? cat}
                  </h3>
                  <ul className="space-y-3">
                    {(grouped[cat] ?? []).map((doc) => (
                      <li
                        key={doc.id}
                        className="flex flex-col gap-4 rounded-lg border border-brand-surface bg-brand-white p-5 shadow-card transition-shadow hover:shadow-card-hover md:flex-row md:items-center"
                      >
                        <div className="flex items-start gap-4 md:flex-1">
                          <span
                            className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-surface text-brand-charcoal"
                            aria-hidden="true"
                          >
                            <FileText size={20} strokeWidth={1.75} />
                          </span>
                          <div>
                            <p className="text-base font-bold text-brand-charcoal">
                              {formatDocTitle(doc)}
                            </p>
                            <p className="mt-1 text-sm text-brand-body-medium">PDF</p>
                          </div>
                        </div>
                        <a
                          href={fileUrl(doc)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={`${doc.title}.pdf`}
                          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 md:w-auto"
                        >
                          Download
                          <ArrowRight size={16} aria-hidden="true" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>
    </>
  );
}
